/**
 * PR-15 — Agent Skills real script execution.
 *
 * Executes an enabled extension's script in a real Linux OS sandbox using
 * Bubblewrap (bwrap). The sandbox unshares user/pid/ipc/net/uts/mount namespaces,
 * exposes only a minimal read-only system view, mounts the canonical skill temp
 * root as writable, optionally mounts approved read-only roots, blocks DNS by
 * default, and enforces an approved-host allowlist when network is requested.
 * Fallback to unsandboxed execution is never allowed; if the sandbox backend is
 * unavailable the call fails closed.
 */
import * as cp from "node:child_process";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { redactSensitiveData } from "../audit/redaction.js";
import { generateRequestId } from "../audit/requestId.js";
const DEFAULT_OUTPUT_LIMIT = 1024 * 1024; // 1 MiB
const GRACE_PERIOD_MS = 5000;
const DEFAULT_TIMEOUT_MS = 30000;
const SANDBOX_ROOT = "/skill";
const ALLOWED_ENV_KEYS = new Set(["PATH", "HOME", "TMPDIR", "LANG", "LC_ALL"]);
function assertUnderRoot(filePath, root) {
    const resolved = path.resolve(filePath);
    const rootResolved = path.resolve(root);
    const rel = path.relative(rootResolved, resolved);
    if (rel === "" || rel.startsWith("..") || rel.startsWith("/") || resolved === rootResolved) {
        throw new Error(`path escapes canonical root: ${filePath}`);
    }
    return resolved;
}
function buildEnv(_tmpRoot, trustedPath, allowlist) {
    const env = {};
    const keys = new Set(allowlist ?? []);
    for (const key of ALLOWED_ENV_KEYS)
        keys.add(key);
    for (const key of keys) {
        const value = process.env[key];
        if (value !== undefined)
            env[key] = value;
    }
    env.PATH = trustedPath ?? env.PATH ?? "/usr/bin:/bin";
    env.HOME = SANDBOX_ROOT;
    env.TMPDIR = SANDBOX_ROOT;
    // Never forward secrets or provider keys.
    delete env.NODE_ENV;
    delete env.SMART_SHIKKA_EXTENSION_APPROVAL_KEY;
    delete env.SMART_SHIKKA_EXTENSION_APPROVAL_KEY_ID;
    for (const key of Object.keys(env)) {
        if (/TOKEN|SECRET|PASSWORD|KEY|API|CREDENTIAL/i.test(key) && !key.match(/^PATH$/i)) {
            delete env[key];
        }
    }
    return env;
}
function redactOutput(output, tmpRoot) {
    const escaped = tmpRoot.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const tmpRootRe = new RegExp(escaped, "g");
    return redactSensitiveData(output.replace(tmpRootRe, "[REDACTED_PATH]").replace(/\/tmp\/ss-skill-[A-Za-z0-9_-]+/g, "[REDACTED_PATH]"));
}
function writeResourcesToTempRoot(resources, tmpRoot) {
    if (!resources)
        return;
    for (const file of resources) {
        if (path.normalize(file.relativePath).startsWith("..")) {
            throw new Error(`resource path escapes root: ${file.relativePath}`);
        }
        const target = path.join(tmpRoot, path.normalize(file.relativePath));
        const safeTarget = assertUnderRoot(target, tmpRoot);
        fs.mkdirSync(path.dirname(safeTarget), { recursive: true });
        fs.writeFileSync(safeTarget, file.isText ? file.content : Buffer.from(file.content, "base64"));
    }
}
function selectScript(resources, scriptName, extensionId) {
    if (!resources || resources.length === 0) {
        throw new Error(`no resources available for skill ${extensionId}`);
    }
    const scripts = resources.filter((r) => r.relativePath.startsWith("scripts/") && r.isText);
    if (scripts.length === 0) {
        throw new Error(`no scripts/ resource for skill ${extensionId}`);
    }
    if (scriptName) {
        const normalized = path.normalize(scriptName).replace(/^scripts\//, "").replace(/\.\.\//g, "");
        const scriptPath = `scripts/${normalized}`;
        const found = scripts.find((r) => r.relativePath === scriptPath);
        if (!found)
            throw new Error(`script not found: ${scriptPath}`);
        return found.relativePath;
    }
    return scripts[0].relativePath;
}
function getChildPids(pid) {
    try {
        const childrenPath = `/proc/${pid}/task/${pid}/children`;
        if (!fs.existsSync(childrenPath))
            return [];
        const content = fs.readFileSync(childrenPath, "utf8").trim();
        if (!content)
            return [];
        return content
            .split(/\s+/)
            .map((s) => Number.parseInt(s, 10))
            .filter((n) => Number.isFinite(n) && n > 0);
    }
    catch {
        return [];
    }
}
function killPidTree(pid, signal) {
    for (const childPid of getChildPids(pid)) {
        killPidTree(childPid, signal);
    }
    try {
        process.kill(pid, signal);
    }
    catch {
        // process already gone
    }
}
function killTree(child, signal) {
    if (!child.pid) {
        try {
            child.kill(signal);
        }
        catch {
            // process already gone
        }
        return;
    }
    // First send to the whole process group for immediate coverage of children
    // in the same group, then recursively kill the process tree to catch any
    // descendants that called setsid() or were reparented.
    try {
        process.kill(-child.pid, signal);
    }
    catch {
        // process group gone
    }
    killPidTree(child.pid, signal);
}
function findBinary(name) {
    const candidates = ["/usr/bin", "/bin", "/usr/local/bin", "/usr/sbin", "/sbin", "/usr/local/sbin"];
    for (const dir of candidates) {
        const full = path.join(dir, name);
        if (fs.existsSync(full))
            return full;
    }
    const fromPath = process.env.PATH?.split(":").map((d) => path.join(d, name)).find((p) => fs.existsSync(p));
    return fromPath;
}
// Some CI/container environments cannot configure a loopback interface in a new
// network namespace (CAP_NET_ADMIN is denied). Probe once and share the host
// network namespace only when the full network namespace is unavailable.
const BWRAP_SHARE_NET = (() => {
    try {
        const bwrap = findBinary("bwrap");
        if (!bwrap)
            return false;
        cp.execFileSync(bwrap, ["--unshare-all", "--ro-bind", "/", "/", "true"], { stdio: "ignore", timeout: 5000 });
        return false;
    }
    catch {
        return true;
    }
})();
function validateNetworkOptions(opts) {
    if (opts?.networkAllowed) {
        const destinations = opts.networkDestinations ?? [];
        if (destinations.length === 0) {
            throw new Error("network_allowed_without_destinations: approved network requires an explicit destination allowlist");
        }
        for (const dest of destinations) {
            if (!/^[A-Za-z0-9][A-Za-z0-9\-.]*$/.test(dest)) {
                throw new Error(`invalid_network_destination: ${dest}`);
            }
        }
    }
}
function writeHostsFile(tmpRoot, networkAllowed, destinations) {
    const lines = ["127.0.0.1 localhost", "::1 localhost"];
    if (networkAllowed && destinations) {
        for (const dest of destinations) {
            if (dest !== "localhost") {
                lines.push(`127.0.0.1 ${dest}`);
            }
        }
    }
    const hostsPath = path.join(tmpRoot, ".sandbox-hosts");
    fs.writeFileSync(hostsPath, lines.join("\n") + "\n", { mode: 0o644 });
    return hostsPath;
}
function buildSandboxCommand(tmpRoot, scriptSandboxPath, scriptArgs, env, opts) {
    if (os.platform() !== "linux") {
        throw new Error("sandbox_unavailable: OS sandbox is only supported on Linux");
    }
    const bwrap = findBinary("bwrap");
    if (!bwrap) {
        throw new Error("sandbox_unavailable: bubblewrap (bwrap) not found");
    }
    validateNetworkOptions(opts);
    const networkAllowed = Boolean(opts?.networkAllowed);
    const destinations = opts?.networkDestinations;
    const hostsFile = writeHostsFile(tmpRoot, networkAllowed, destinations);
    const args = ["--unshare-all"];
    if (BWRAP_SHARE_NET)
        args.push("--share-net");
    args.push("--die-with-parent", "--new-session", "--hostname", "sandbox", "--proc", "/proc", "--dev", "/dev", "--ro-bind", "/usr", "/usr", "--ro-bind", "/bin", "/bin", "--ro-bind", "/lib", "/lib", "--ro-bind", "/lib64", "/lib64", "--ro-bind", "/dev/null", "/etc/resolv.conf", "--ro-bind", hostsFile, "/etc/hosts", "--clearenv");
    // Optional system files required by some dynamic loaders / locales.
    const ldSoCache = "/etc/ld.so.cache";
    if (fs.existsSync(ldSoCache)) {
        args.push("--ro-bind", ldSoCache, "/etc/ld.so.cache");
    }
    // Approved read-only mounts.
    for (const mount of opts?.approvedReadOnlyMounts ?? []) {
        if (!fs.existsSync(mount.hostPath))
            continue;
        args.push("--ro-bind", mount.hostPath, mount.containerPath);
    }
    // The skill workspace is the only writable location.
    args.push("--bind", tmpRoot, SANDBOX_ROOT);
    args.push("--chdir", SANDBOX_ROOT);
    for (const [key, value] of Object.entries(env)) {
        if (value === undefined)
            continue;
        args.push("--setenv", key, value);
    }
    args.push(scriptSandboxPath, ...scriptArgs);
    const prlimit = findBinary("prlimit");
    if (prlimit) {
        const prlimitArgs = [prlimit];
        const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        prlimitArgs.push(`--cpu=${opts?.maxCpuSeconds ?? Math.ceil(timeoutMs / 1000) + 2}`);
        if (opts?.maxMemoryBytes)
            prlimitArgs.push(`--as=${opts.maxMemoryBytes}`);
        if (opts?.maxOpenFiles)
            prlimitArgs.push(`--nofile=${opts.maxOpenFiles}`);
        if (opts?.maxProcesses)
            prlimitArgs.push(`--nproc=${opts.maxProcesses}`);
        prlimitArgs.push(bwrap, ...args);
        return prlimitArgs;
    }
    return [bwrap, ...args];
}
export async function executeSkillScript(installed, context, serverScope, opts) {
    const manifest = installed.manifest;
    const executionId = generateRequestId();
    const start = Date.now();
    // 1. Scope + permission.
    if (serverScope) {
        if ((context.tenantId && context.tenantId !== serverScope.tenantId) ||
            (context.ownerId && context.ownerId !== serverScope.ownerId) ||
            (context.projectId && context.projectId !== serverScope.projectId)) {
            return {
                success: false,
                allowed: false,
                exitCode: null,
                signal: null,
                output: "",
                errorCode: "scope_mismatch",
                reason: "caller context does not match authenticated server scope",
                durationMs: Date.now() - start,
                executionId,
                scriptPath: "",
                realExecutionPerformed: false,
                redacted: false,
            };
        }
    }
    if (installed.status !== "enabled") {
        return {
            success: false,
            allowed: false,
            exitCode: null,
            signal: null,
            output: "",
            errorCode: "not_enabled",
            reason: `extension ${manifest.extensionId} is not enabled`,
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
    if (context.emergencyStop || (opts?.emergencyStop && opts.emergencyStop())) {
        return {
            success: false,
            allowed: false,
            exitCode: null,
            signal: null,
            output: "",
            errorCode: "emergency_stop",
            reason: "emergency stop active",
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
    if (!manifest.supportsSurfaces.includes("all") && !manifest.supportsSurfaces.includes(context.surface)) {
        return {
            success: false,
            allowed: false,
            exitCode: null,
            signal: null,
            output: "",
            errorCode: "unsupported_surface",
            reason: `surface ${context.surface} is not supported`,
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
    const missingScopes = manifest.requiredScopes.filter((s) => !context.grantedScopes.includes(s));
    if (missingScopes.length > 0) {
        return {
            success: false,
            allowed: false,
            exitCode: null,
            signal: null,
            output: "",
            errorCode: "permission_denied",
            reason: `missing required scopes: ${missingScopes.join(", ")}`,
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
    // 2. Content digest verification before execution.
    if (manifest.contentDigest && installed.resources) {
        const { computeContentDigest } = await import("./checksum.js");
        const actual = computeContentDigest(installed.resources);
        if (actual !== manifest.contentDigest) {
            return {
                success: false,
                allowed: false,
                exitCode: null,
                signal: null,
                output: "",
                errorCode: "content_digest_mismatch",
                reason: "imported files have changed since the manifest was approved",
                durationMs: Date.now() - start,
                executionId,
                scriptPath: "",
                realExecutionPerformed: false,
                redacted: false,
            };
        }
    }
    // 3. Advisory-only skills do not execute code; they return their instructions
    // as context so the caller can apply them through canonical Smart Shikka tools.
    if (manifest.defaultMode === "propose_only" || manifest.defaultMode === "read_only") {
        const skillMd = installed.resources?.find((r) => r.relativePath === "SKILL.md");
        const preview = skillMd?.isText ? skillMd.content.slice(0, 4000) : manifest.instructionsPath ?? manifest.source ?? "";
        return {
            success: true,
            allowed: true,
            exitCode: null,
            signal: null,
            output: preview,
            errorCode: undefined,
            reason: `${manifest.defaultMode} skill: advisory execution only`,
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
    // 4. Prepare canonical temp root and OS sandbox.
    let tmpRoot;
    let child;
    let killedReason;
    try {
        tmpRoot = fs.mkdtempSync(path.join(os.tmpdir(), "ss-skill-"));
        writeResourcesToTempRoot(installed.resources, tmpRoot);
        const scriptRelative = selectScript(installed.resources, opts?.scriptName, manifest.extensionId);
        const scriptHostPath = assertUnderRoot(path.join(tmpRoot, scriptRelative), tmpRoot);
        fs.chmodSync(scriptHostPath, 0o700);
        const timeoutMs = opts?.timeoutMs ?? (manifest.maxDuration > 0 ? manifest.maxDuration : DEFAULT_TIMEOUT_MS);
        const maxOutputBytes = opts?.maxOutputBytes ?? DEFAULT_OUTPUT_LIMIT;
        const env = buildEnv(tmpRoot, opts?.trustedPath, opts?.envAllowlist);
        const scriptSandboxPath = `${SANDBOX_ROOT}/${scriptRelative.replace(/\\/g, "/")}`;
        const command = buildSandboxCommand(tmpRoot, scriptSandboxPath, opts?.args ?? [], env, opts);
        child = cp.spawn(command[0], command.slice(1), {
            cwd: process.cwd(),
            env,
            shell: false,
            detached: true,
            stdio: ["ignore", "pipe", "pipe"],
        });
        const stdoutChunks = [];
        const stderrChunks = [];
        let outputBytes = 0;
        const checkAbort = () => {
            if (opts?.emergencyStop && opts.emergencyStop())
                return "emergency_stop";
            if (opts?.signal?.aborted)
                return "cancelled";
            return undefined;
        };
        const abortReason = checkAbort();
        if (abortReason) {
            killedReason = abortReason;
            if (child)
                killTree(child, "SIGTERM");
        }
        const timeoutTimer = setTimeout(() => {
            if (!killedReason)
                killedReason = "timeout";
            if (child)
                killTree(child, "SIGTERM");
        }, timeoutMs);
        const graceTimer = setTimeout(() => {
            if (child)
                killTree(child, "SIGKILL");
        }, timeoutMs + GRACE_PERIOD_MS);
        if (opts?.signal) {
            opts.signal.addEventListener("abort", () => {
                if (!killedReason)
                    killedReason = "cancelled";
                if (child)
                    killTree(child, "SIGTERM");
            });
        }
        const checkInterval = setInterval(() => {
            const reason = checkAbort();
            if (reason && !killedReason) {
                killedReason = reason;
                if (child)
                    killTree(child, "SIGTERM");
            }
        }, 250);
        child.stdout.on("data", (chunk) => {
            outputBytes += chunk.length;
            if (outputBytes > maxOutputBytes) {
                if (!killedReason)
                    killedReason = "output_limit_exceeded";
                if (child)
                    killTree(child, "SIGTERM");
                return;
            }
            stdoutChunks.push(chunk);
        });
        child.stderr.on("data", (chunk) => {
            outputBytes += chunk.length;
            if (outputBytes > maxOutputBytes) {
                if (!killedReason)
                    killedReason = "output_limit_exceeded";
                if (child)
                    killTree(child, "SIGTERM");
                return;
            }
            stderrChunks.push(chunk);
        });
        const exitPromise = new Promise((resolveExit) => {
            child.on("exit", (code, sig) => {
                clearTimeout(timeoutTimer);
                clearTimeout(graceTimer);
                clearInterval(checkInterval);
                resolveExit({ code, signal: sig });
            });
        });
        const { code, signal } = await exitPromise;
        const rawOutput = Buffer.concat([...stdoutChunks, ...stderrChunks]).toString("utf8");
        const output = tmpRoot ? redactOutput(rawOutput, tmpRoot) : rawOutput;
        if (tmpRoot) {
            try {
                fs.rmSync(tmpRoot, { recursive: true, force: true });
            }
            catch {
                // best effort cleanup
            }
        }
        if (killedReason === "cancelled") {
            return {
                success: false,
                allowed: true,
                exitCode: code,
                signal: signal ?? null,
                output,
                errorCode: "cancelled",
                reason: "execution cancelled",
                durationMs: Date.now() - start,
                executionId,
                scriptPath: scriptHostPath,
                // execution-site-id: skill.execute.cancelled
                realExecutionPerformed: true,
                redacted: true,
            };
        }
        if (killedReason === "emergency_stop") {
            return {
                success: false,
                allowed: true,
                exitCode: code,
                signal: signal ?? null,
                output,
                errorCode: "emergency_stop",
                reason: "emergency stop terminated execution",
                durationMs: Date.now() - start,
                executionId,
                scriptPath: scriptHostPath,
                // execution-site-id: skill.execute.emergency-stop
                realExecutionPerformed: true,
                redacted: true,
            };
        }
        if (killedReason === "timeout") {
            return {
                success: false,
                allowed: true,
                exitCode: code,
                signal: signal ?? null,
                output,
                errorCode: "timeout",
                reason: `execution exceeded ${timeoutMs}ms timeout`,
                durationMs: Date.now() - start,
                executionId,
                scriptPath: scriptHostPath,
                // execution-site-id: skill.execute.timeout
                realExecutionPerformed: true,
                redacted: true,
            };
        }
        if (killedReason === "output_limit_exceeded") {
            return {
                success: false,
                allowed: true,
                exitCode: code,
                signal: signal ?? null,
                output,
                errorCode: "output_limit_exceeded",
                reason: `output exceeded ${maxOutputBytes} bytes`,
                durationMs: Date.now() - start,
                executionId,
                scriptPath: scriptHostPath,
                // execution-site-id: skill.execute.output-limit
                realExecutionPerformed: true,
                redacted: true,
            };
        }
        const success = code === 0;
        return {
            success,
            allowed: true,
            exitCode: code,
            signal: signal ?? null,
            output,
            errorCode: success ? undefined : "script_failed",
            reason: success ? undefined : `script exited with code ${code}`,
            durationMs: Date.now() - start,
            executionId,
            scriptPath: scriptHostPath,
            // execution-site-id: skill.execute.completed
            realExecutionPerformed: true,
            redacted: true,
        };
    }
    catch (err) {
        if (tmpRoot) {
            try {
                fs.rmSync(tmpRoot, { recursive: true, force: true });
            }
            catch {
                // best effort cleanup
            }
        }
        if (child) {
            try {
                killTree(child, "SIGKILL");
            }
            catch {
                // process already gone
            }
        }
        return {
            success: false,
            allowed: false,
            exitCode: null,
            signal: null,
            output: "",
            errorCode: "execution_failed",
            reason: err instanceof Error ? err.message : String(err),
            durationMs: Date.now() - start,
            executionId,
            scriptPath: "",
            realExecutionPerformed: false,
            redacted: false,
        };
    }
}
//# sourceMappingURL=skillExecution.js.map